import * as z from 'zod'
import { css, keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { zodResolver } from '@hookform/resolvers/zod'
import { Reorder, useDragControls } from 'framer-motion'
import Image from 'next/image'
import React from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useEffectOnce } from 'react-use'
import isURL from 'validator/lib/isURL'

import { isEmpty } from '@livechat/data-utils'

import { useKeyboardReordering } from '../../../hooks/useKeyboardReordering'
import { usePreviewController } from '../../WidgetPreview/previewController'
import NewLinkForm from './NewLinkForm'
import Button from 'components/Button'
import { FeatureExplanation } from 'components/FeaturesList'
import { ToastKind } from 'components/Toast'
import Form from 'components/fields/Form'
import Input from 'components/fields/Input'
import { DragIcon, TrashIcon } from 'components/icons'
import { useFeature } from 'hooks/useFeature'
import { useIsOnboarding } from 'hooks/useIsOnboarding'
import { useTracking } from 'hooks/useTracking'
import { updateFeature, updateOrganization } from 'lib/api'
import { ensureProtocol, isMobile } from 'lib/helpers'
import { Store, useAppStore } from 'lib/store'
import { EVENT_NAMES } from 'lib/tracking'
import { type Feature } from 'types/feature'

const LIMIT_LINKS = 5

const LinkCard = styled(Reorder.Item)<{ isDragging: boolean }>`
	padding: ${({ theme }) => theme.spaces.space5};
	padding-top: 0;
	background-color: white;
	border-radius: ${({ theme }) => theme.borderRadiuses.xl};
	margin-bottom: ${({ theme }) => theme.spaces.space6};
	box-shadow: ${({ theme }) => theme.boxShadows.subtle};
	height: 185px;
	width: 80%;
	list-style: none;
	position: relative;

	${({ isDragging, theme }) =>
		isDragging &&
		css`
			box-shadow: ${theme.boxShadows.floating};
			scale: 1.05;
		`}
`

const StyledReorderGroup = styled(Reorder.Group)`
	padding: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
`

const LinkCardInnerColumn = styled.div`
	display: flex;
	flex-direction: column;
`

const StyledLinksForm = styled(Form)`
	max-width: 380px;
	padding-top: ${({ theme }) => theme.spaces.space3};
	padding-bottom: 32px;
`

const DeleteLinkButton = styled(Button)`
	position: absolute;
	top: 10px;
	right: -33px;
	background-color: transparent;
`

const ReorderButton = styled(DragIcon)`
	position: absolute;
	top: 10px;
	left: -25px;
	cursor: grab;
`

const Overlay = styled.div`
	position: absolute;
	inset: 0;
	z-index: 1;
	background: linear-gradient(180deg, #fafafa 0%, rgba(255, 255, 255, 0) 50%, #fafafa 100%);
`

const scroll = keyframes`
  0% {
    transform: translateY(0px)
  }
  100% {
    transform: translateY(-244px)
  }
`

const AnimatedImage = styled(Image)`
	animation: ${scroll} 10s linear infinite;
	width: 100%;
	max-width: 380px;
	height: auto !important;
`

const LinksExampleOuter = styled.div`
	width: 100%;
	height: 270px;
	overflow: hidden;
	position: relative;
`

const FormSchema = z.object({
	links: z.array(
		z.object({
			url: z.string().refine(isURL, 'Invalid URL format'),
			text: z
				.string()
				.nonempty({ message: 'This field is required' })
				.max(60, { message: 'Cannot be longer than 60 characters' }),
		}),
	),
})

function syncStoreWithForm(store: Store, formValues: Partial<FormValues>) {
	if (!formValues?.links || formValues.links.length === 0) {
		return
	}

	store.dispatch({
		type: 'links/set',
		payload: {
			enabled: true,
			properties: {
				links: formValues.links,
			},
		},
	})
}

type FormValues = z.infer<typeof FormSchema>

function withDefaultValues(FormComponent: typeof LinksForm) {
	const WithDefaultValues = React.forwardRef<HTMLFormElement, Props>((props, ref) => {
		const { feature: linksFeature, isFeatureLoading: areLinksLoading } = useFeature('links')

		if (areLinksLoading) {
			return null
		}

		return (
			<FormComponent
				{...props}
				ref={ref}
				defaultValues={{
					links: linksFeature?.properties.links ?? [],
				}}
			/>
		)
	})

	WithDefaultValues.displayName = `WithDefaultValues(${FormComponent.displayName})`
	return WithDefaultValues
}

function LinkCardWrapper({
	fieldId,
	handleDragEnd,
	handleKeyboardReorder,
	children,
}: {
	fieldId: string
	children: React.ReactNode
	handleDragEnd: () => void
	handleKeyboardReorder: (event: React.KeyboardEvent) => void
}) {
	const controls = useDragControls()
	const [isDragging, setIsDragging] = React.useState(false)

	return (
		<LinkCard
			value={fieldId}
			dragControls={controls}
			dragListener={isMobile()}
			onDragEnd={() => {
				handleDragEnd()
				setIsDragging(false)
			}}
			data-testid="custom-link-card"
			isDragging={isDragging}
		>
			<ReorderButton
				key={fieldId}
				role="button"
				tabIndex={0}
				onPointerDown={(event) => {
					controls.start(event)
					setIsDragging(true)
				}}
				onKeyDown={handleKeyboardReorder}
				aria-label="Change link position"
			/>
			{children}
		</LinkCard>
	)
}

type Props = {
	id: string
	onSubmit?: VoidFunction
	onIsValidChanged?(isValid: boolean, id: string): void
	onHasErrorsChanged?(hasErrors: boolean, id: string): void
	onIsDirtyChanged?(isDirty: boolean, id: string): void
	onIsSubmittingChanged?(isSubmitting: boolean, id: string): void
	onSubmitResult?(result: ToastKind): void
}

const LinksForm = React.forwardRef<HTMLFormElement, Props & { defaultValues: FormValues }>(
	(
		{
			id,
			defaultValues,
			onIsValidChanged,
			onHasErrorsChanged,
			onIsDirtyChanged,
			onIsSubmittingChanged,
			onSubmit,
			onSubmitResult,
		},
		ref,
	) => {
		const store = useAppStore()
		const track = useTracking()
		const { links } = store.getState()
		const previewController = usePreviewController()
		const { isFeatureLoading: areLinksLoading, mutateFeature: mutateLinksFeature, feature } = useFeature('links')
		const { control, register, handleSubmit, formState, getValues, watch, resetField, reset } = useForm<FormValues>({
			mode: 'onTouched',
			resolver: zodResolver(FormSchema),
			defaultValues,
		})
		const { isValid, isSubmitting, errors, dirtyFields } = formState
		const { fields, prepend, remove, move } = useFieldArray({ control, name: 'links' })
		const hasErrors = !isEmpty(errors)
		const isDirty = !isEmpty(dirtyFields)
		const hasLinks = fields.length > 0
		const isLinksLimitReached = !(fields.length < LIMIT_LINKS)
		const isOnboarding = useIsOnboarding()

		const getLinkId = React.useCallback(
			(item: { text: string; url: string; id?: string }) => {
				const matchingLinks =
					feature?.properties.links?.filter((link) => link.text === item.text && link.url === item.url) ?? []
				const uniqueSuffix = matchingLinks.length === 1 ? '1' : item.id
				return `${item.text}_${item.url}_${uniqueSuffix}`
			},
			[feature],
		)
		const [fieldsOrder, setFieldsOrder] = React.useState(fields.map(getLinkId))

		React.useEffect(() => {
			setFieldsOrder(fields.map(getLinkId))
		}, [fields, getLinkId])

		React.useEffect(() => {
			onIsValidChanged?.(isValid, id)
		}, [id, isValid, onIsValidChanged])

		React.useEffect(() => {
			onHasErrorsChanged?.(hasErrors, id)
		}, [id, onHasErrorsChanged, hasErrors])

		React.useEffect(() => {
			onIsDirtyChanged?.(isDirty, id)
		}, [id, isDirty, onIsDirtyChanged])

		React.useEffect(() => {
			onIsSubmittingChanged?.(isSubmitting, id)
		}, [id, isSubmitting, onIsSubmittingChanged])

		useEffectOnce(() => {
			syncStoreWithForm(store, getValues())
			const { unsubscribe } = watch((formValues) => {
				syncStoreWithForm(store, formValues as Partial<FormValues>)
			})
			return () => {
				unsubscribe()
			}
		})

		const handleRemoveLink = (index: number) => {
			remove(index)
			const filteredLinks = links?.properties?.links?.filter((_, idx) => idx !== index)

			if (isEmpty(filteredLinks ?? [])) {
				store.dispatch({
					type: 'links/set',
					payload: { enabled: false, properties: {} },
				})

				return
			}

			store.dispatch({
				type: 'links/set',
				payload: {
					properties: { links: filteredLinks },
				},
			})
		}

		const handleReset = (event: React.FormEvent) => {
			event.preventDefault()
			reset(defaultValues)
		}

		const handleAddLink = (url: string) => {
			if (!url) {
				return
			}

			const validUrl = ensureProtocol(url)

			track(EVENT_NAMES.CUSTOM_LINK_ADDED, { url: validUrl })

			prepend({ url: validUrl, text: '' })

			store.dispatch({
				type: 'links/set',
				payload: {
					enabled: true,
					properties: {
						links: links?.properties?.links
							? [...links.properties.links, { url: validUrl, text: '' }]
							: [{ url: validUrl, text: '' }],
					},
				},
			})
			previewController.showView('Homescreen', 'custom-links-list')
		}

		const _onSubmit: Parameters<typeof handleSubmit>['0'] = async (data: FormValues) => {
			if (!isValid || (!isOnboarding && !isDirty)) {
				return
			}

			track(EVENT_NAMES.CUSTOM_LINKS_SAVED, {
				hasAnyLinks: fields.length > 0,
				quantity: String(fields.length),
			})

			try {
				const [updatedFeature] = await Promise.all([
					updateFeature('links', {
						enabled: Boolean(data.links.length),
						properties: {
							...(data.links.length > 0 && { links: data.links }),
						},
					}),

					isOnboarding && updateOrganization({ onboardingProgress: 'custom-links' }),
				])

				mutateLinksFeature(updatedFeature, { revalidate: false })
				reset({ links: updatedFeature.properties.links })
				onSubmit?.()
				onSubmitResult?.('success')
			} catch {
				onSubmitResult?.('error')
			}
		}

		const updateLinksOrder = (newFieldsOrder: string[]) => {
			const reorderedLinks = newFieldsOrder.map((fieldId) => {
				const fieldIndex = fields.findIndex((field) => getLinkId(field) === fieldId)
				return links?.properties?.links?.[fieldIndex]
			}) as Feature<'links'>['properties']['links']

			store.dispatch({
				type: 'links/set',
				payload: {
					properties: { links: reorderedLinks },
				},
			})

			const isOrderSameAsDefault = JSON.stringify(defaultValues.links) === JSON.stringify(reorderedLinks)

			if (isOrderSameAsDefault) {
				resetField('links')
			}
		}

		const handleDragEnd = (fieldId: string) => {
			const initIndex = fields.findIndex((field) => getLinkId(field) === fieldId)
			const destinationIndex = fieldsOrder.findIndex((id) => id === fieldId)

			if (initIndex === destinationIndex) {
				return
			}
			move(initIndex, destinationIndex)
			updateLinksOrder(fieldsOrder)
		}

		const { getKeyDownListener } = useKeyboardReordering({
			fieldsOrder,
			itemsLength: fields.length,
			onReorder: (newFieldsOrder, fieldId) => {
				const initIndex = fields.findIndex((field) => getLinkId(field) === fieldId)
				const destinationIndex = newFieldsOrder.findIndex((id) => id === fieldId)
				move(initIndex, destinationIndex)
				updateLinksOrder(newFieldsOrder)
			},
		})

		if (areLinksLoading) {
			return null
		}

		return (
			<>
				{!isOnboarding && (
					<FeatureExplanation>
						Guide users to help centers, meeting scheduling, or social media. Paste the link below to add it to your
						widget.
					</FeatureExplanation>
				)}
				<NewLinkForm onAddNewLink={handleAddLink} isLinksLimitReached={isLinksLimitReached} />
				<StyledLinksForm id={id} onSubmit={handleSubmit(_onSubmit)} onReset={handleReset} ref={ref}>
					{hasLinks && (
						<StyledReorderGroup axis="y" values={fieldsOrder} onReorder={setFieldsOrder} layoutScroll>
							{fieldsOrder?.map((fieldId) => {
								const fieldIndex = fields.findIndex((field) => getLinkId(field) === fieldId)
								return (
									<LinkCardWrapper
										key={fieldId}
										fieldId={fieldId}
										handleDragEnd={() => handleDragEnd(fieldId)}
										handleKeyboardReorder={getKeyDownListener(fieldId)}
									>
										<LinkCardInnerColumn>
											<Input
												label="Name your link"
												compact
												autoFocus={!getValues(`links.${fieldIndex}.text`)}
												error={errors?.links?.[fieldIndex]?.text?.message}
												placeholder='e.g. "Schedule a call"'
												{...register(`links.${fieldIndex}.text`)}
											/>
											<Input
												label="URL"
												compact
												error={errors?.links?.[fieldIndex]?.url?.message}
												placeholder="https://example.com"
												{...register(`links.${fieldIndex}.url`)}
											/>
										</LinkCardInnerColumn>
										<DeleteLinkButton
											type="button"
											kind="surface"
											compact
											onClick={() => handleRemoveLink(fieldIndex)}
											aria-label="Delete custom link"
										>
											<TrashIcon />
										</DeleteLinkButton>
									</LinkCardWrapper>
								)
							})}
						</StyledReorderGroup>
					)}
				</StyledLinksForm>
				{!hasLinks && (
					<LinksExampleOuter>
						<Overlay />
						<AnimatedImage
							src="/images/custom-links-empty.png"
							alt="Custom links examples: schedule a call, watch video guide or follow us on X"
							layout="fill"
						/>
					</LinksExampleOuter>
				)}
			</>
		)
	},
)

LinksForm.displayName = 'LinksForm'

export default withDefaultValues(LinksForm)
